/**
 * PURE client-side labels for the defects dashboard (Story #37/37b). Honesty:
 * `describeReviewState` collapses EVERY input to "Potential — needs review" so no
 * value (now or from #38) can render "Confirmed" on this read-only surface.
 */

export function describeReviewState(state: string): { label: string } {
  // 37a only emits 'new'; anything else is still pre-confirmation on this surface.
  switch (state) {
    case 'new':
      return { label: 'Potential — needs review' };
    default:
      return { label: 'Potential — needs review' };
  }
}

export function describeReasonCode(reasonCode: string): string {
  switch (reasonCode) {
    case 'assert_failed':
      return 'Assertion failed';
    default:
      return reasonCode.replace(/[-_]/g, ' ');
  }
}

/** The six human-settable review states, in the order offered in the UI. Mirrors
 * the backend `HUMAN_REVIEW_STATES` (`new` is system-only, never offered). */
export const HUMAN_REVIEW_STATES = [
  'needs_investigation',
  'confirmed',
  'false_positive',
  'duplicate',
  'resolved',
  'ignored',
] as const;

export type HumanReviewState = (typeof HUMAN_REVIEW_STATES)[number];

/**
 * HONEST detail-view label for a review state (Story #38, slice 38b). Unlike the
 * list's {@link describeReviewState} (which collapses everything to "Potential"),
 * the detail view shows the real ruled state — but "Confirmed defect" appears
 * ONLY for `confirmed`, and `new`/`needs_investigation` still read as potential.
 */
export const REVIEW_STATE_LABELS: Record<string, string> = {
  new: 'New — potential, needs review',
  needs_investigation: 'Needs investigation',
  confirmed: 'Confirmed defect',
  false_positive: 'Not a defect (false positive)',
  duplicate: 'Duplicate',
  resolved: 'Resolved',
  ignored: 'Ignored',
};

export function describeDetailReviewState(state: string): string {
  return REVIEW_STATE_LABELS[state] ?? 'Potential — needs review';
}

/** Human label for each offered target state (drives the radio group). */
export const REVIEW_TARGET_LABELS: Record<HumanReviewState, string> = {
  needs_investigation: 'Needs investigation',
  confirmed: 'Confirm defect',
  false_positive: 'Mark as false positive',
  duplicate: 'Mark as duplicate',
  resolved: 'Mark as resolved',
  ignored: 'Ignore',
};

/** Client mirror of the server rule: a note is required ONLY to ignore a
 * critical/high defect. The server remains authoritative. */
export function noteRequired(severity: string, toState: string): boolean {
  return toState === 'ignored' && (severity === 'critical' || severity === 'high');
}

/** Client mirror: a canonical defect is required to mark as duplicate. */
export function canonicalRequired(toState: string): boolean {
  return toState === 'duplicate';
}

/** Where a review error should surface + its honest copy. */
export interface ReviewErrorCopy {
  message: string;
  placement: 'form' | 'note' | 'canonical';
  /** True for a 409 stale-write — the caller should re-fetch the defect. */
  conflict?: boolean;
  requestId?: string;
}

/**
 * Maps a review submission error to honest, specific copy. Keys on HTTP status +
 * the stable machine TOKEN the backend puts in the envelope `message`
 * (`note_required`, `invalid_canonical`, `state_conflict`, …) — a token, never
 * localized prose. Anything unmapped falls back to a generic form-level message.
 */
export function reviewErrorCopy(status: number, token: string, requestId?: string): ReviewErrorCopy {
  const ref = requestId ? { requestId } : {};
  if (status === 409) {
    return {
      message:
        'This defect changed since you opened it. We refreshed it — review the current state and try again.',
      placement: 'form',
      conflict: true,
      ...ref,
    };
  }
  if (status === 403) {
    return { message: "You don't have permission to review defects.", placement: 'form', ...ref };
  }
  if (status === 404) {
    return { message: "We couldn't find this defect.", placement: 'form', ...ref };
  }
  if (status === 422) {
    switch (token) {
      case 'note_required':
        return {
          message: 'Add a note explaining why you are ignoring this critical or high defect.',
          placement: 'note',
        };
      case 'canonical_required':
        return { message: 'Choose which defect this is a duplicate of.', placement: 'canonical' };
      case 'invalid_canonical':
        return {
          message: 'That defect can’t be used as the canonical — choose another.',
          placement: 'canonical',
        };
      case 'no_op':
        return {
          message: 'This defect is already in that state. Choose a different one.',
          placement: 'form',
        };
      case 'illegal_target':
        return {
          message: 'That isn’t a valid next state for this defect. Pick a different option.',
          placement: 'form',
        };
      default:
        return { message: 'Please check your entries and try again.', placement: 'form' };
    }
  }
  return { message: 'Something went wrong. Please try again.', placement: 'form', ...ref };
}
